import { type FC, useContext, useEffect } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';

import { FormattedMessage, defineMessages, useIntl, type IntlShape } from 'dibs-react-intl';
import PersonBubble from 'dibs-icons/exports/legacy/PersonBubble.ts';
import Message from 'dibs-icons/exports/legacy/Message.ts';
import WhatsApp from 'dibs-icons/exports/legacy/WhatsApp.ts';
import { useSupportChat } from 'dibs-support-chat/exports/useSupportChat.ts';

import { GetHelpZendeskContactSupportLink } from './GetHelpZendeskContactSupportLink.tsx';
import { GetHelpZendeskSendMessageButton } from './GetHelpZendeskSendMessageButton.tsx';

import { getAvailability } from '../OtherHelp.tsx';
import { getSupportNumber } from '../helpers.tsx';
import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { RENDER_AS, STEPS } from './GetHelpZendeskConstants.ts';
import {
    trackChatInitiated,
    trackWhatsAppClick,
    trackSMSClick,
    trackLiveChatDisplayed,
} from '../tracking.ts';

import { type GetHelpZendeskContactSupport_viewer$key } from './__generated__/GetHelpZendeskContactSupport_viewer.graphql.ts';

import dibsCss from 'dibs-css';

const messages = defineMessages({
    defaultMessageText: {
        id: 'getHelp.GetHelpZendeskContactSupport.defaultMessageText',
        defaultMessage: 'Hello, I need help with Order {orderId}',
    },
});

const getDefaultMessageText = ({
    orderId,
    intl,
}: {
    orderId?: string;
    intl: IntlShape;
}): string => {
    let message = '';

    if (orderId) {
        message = intl.formatMessage(messages.defaultMessageText, { orderId });
    }

    return encodeURI(message);
};

const GetHelpZendeskContactSupport: FC<{
    viewer: GetHelpZendeskContactSupport_viewer$key;
    showHeader?: boolean;
}> = ({ viewer: viewerRef, showHeader = true }) => {
    const intl = useIntl();
    const {
        state: {
            isEmbedded,
            isSeller,
            onClose,
            orderId,
            itemId,
            renderAs,
            showAuthModal,
            topicKey,
            stepsPath,
            placement,
        },
        dispatch,
    } = useContext(GetHelpZendeskContext);

    const viewer = useFragment(
        graphql`
            fragment GetHelpZendeskContactSupport_viewer on Viewer {
                loggedInBuyerSupportChat: supportChat(touchpoint: Buyer_Messaging)
                    @include(if: $hasUserId) {
                    ...useSupportChat_supportChat
                }
                loggedOutBuyerSupportChat: supportChat(touchpoint: Guest_Buyer_Messaging)
                    @include(if: $isLoggedOutUser) {
                    ...useSupportChat_supportChat
                }
                sellerSupportChat: supportChat(touchpoint: Seller_Messaging)
                    @include(if: $hasSellerId) {
                    ...useSupportChat_supportChat
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    ...useSupportChat_user
                }
                seller(sellerId: $sellerId) @include(if: $hasSellerId) {
                    ...useSupportChat_seller
                }
                zendeskArticles(isSeller: $isSeller, sellerId: $sellerId) {
                    topicKeyForArticle(articleId: $articleId)
                }
            }
        `,
        viewerRef
    );

    const {
        zendeskArticles,
        loggedInBuyerSupportChat,
        loggedOutBuyerSupportChat,
        sellerSupportChat,
        user,
        seller,
    } = viewer;
    const buyerSupportChat = user ? loggedInBuyerSupportChat : loggedOutBuyerSupportChat;

    const {
        isEnabled: isChatEnabled,
        isChatAvailable,
        isLiveAgentAvailable,
        initSupportChat,
    } = useSupportChat({
        supportChat: isSeller ? sellerSupportChat : buyerSupportChat,
        ...(isSeller ? { seller } : { user }),
    });

    const availabilityCopy =
        getAvailability({ isAgentAvailable: isLiveAgentAvailable, intl }) || '';

    const lastStepWithTopic = [...stepsPath]
        .reverse()
        .find(({ step }) => step === STEPS.ARTICLE || step === STEPS.CMS_TOPIC);

    const topicValue =
        lastStepWithTopic?.step === STEPS.CMS_TOPIC
            ? topicKey
            : zendeskArticles?.topicKeyForArticle;

    useEffect(() => {
        if (!availabilityCopy) {
            return;
        }

        trackLiveChatDisplayed({ placement, isAgentAvailable: isChatAvailable });
    }, [availabilityCopy, isSeller, placement, isChatAvailable]);

    return (
        <div className={dibsCss.mbLarge}>
            {showHeader && (
                <>
                    <h1 className={dibsCss.sassyFontHeaderMedium}>
                        <FormattedMessage
                            id="getHelp.GetHelpZendeskContactSupport.title"
                            defaultMessage="Contact Support"
                        />
                    </h1>
                    <div className={classnames(dibsCss.sassyFontBodyMedium, dibsCss.mbSmall)}>
                        <FormattedMessage
                            id="getHelp.GetHelpZendeskContactSupport.info"
                            defaultMessage="Selected how you want to contact us"
                        />
                    </div>
                </>
            )}
            {availabilityCopy && (
                <>
                    <GetHelpZendeskContactSupportLink
                        title={
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskContactSupport.liveChatLabel"
                                defaultMessage="Live Chat"
                            />
                        }
                        Icon={PersonBubble}
                        availability={
                            getAvailability({
                                isAgentAvailable: isChatAvailable,
                                isChat: true,
                                intl,
                            }) || ''
                        }
                        showOnline={isChatAvailable}
                        disabled={!isChatEnabled}
                        onClick={() => {
                            if (renderAs === RENDER_AS.LOGGED_OUT) {
                                if (showAuthModal) {
                                    showAuthModal({
                                        topicValue: topicValue || '',
                                        isChatFlow: true,
                                        onSuccess: () => dispatch({ type: 'SET_BUYER' }),
                                    });
                                }
                            } else {
                                initSupportChat({
                                    openWindow: true,
                                    showChatButtonOnLoad: false,
                                    touchpoint: isSeller ? 'Seller_Messaging' : 'Buyer_Messaging',
                                    locale: intl.locale,
                                    preChatFields: {
                                        pageType: 'get help',
                                        topic:
                                            topicValue ||
                                            (isSeller
                                                ? 'sellingGeneralQuestions'
                                                : 'itemQuestions'),
                                        itemId,
                                        orderId,
                                    },
                                });
                                trackChatInitiated(topicValue || '');

                                if (!isEmbedded) {
                                    onClose({ isClosedAutomatically: true });
                                }
                            }
                        }}
                    />
                    <GetHelpZendeskContactSupportLink
                        title={
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskContactSupport.whatsAppLabel"
                                defaultMessage="WhatsApp"
                            />
                        }
                        Icon={WhatsApp}
                        availability={availabilityCopy}
                        showOnline={isLiveAgentAvailable}
                        href={`https://wa.me/${getSupportNumber()}?text=${getDefaultMessageText({
                            orderId,
                            intl,
                        })}`}
                        onClick={() => trackWhatsAppClick('')}
                    />
                    <GetHelpZendeskContactSupportLink
                        title={
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskContactSupport.textMessageLabel"
                                defaultMessage="Text Message"
                            />
                        }
                        Icon={Message}
                        availability={availabilityCopy}
                        showOnline={isLiveAgentAvailable}
                        href={`sms:+${getSupportNumber()}?&body=${getDefaultMessageText({
                            orderId,
                            intl,
                        })}`}
                        onClick={() => trackSMSClick('')}
                    />
                </>
            )}
            <GetHelpZendeskSendMessageButton topicValue={topicValue || ''} />
        </div>
    );
};

export { GetHelpZendeskContactSupport };
